/**
 * Resolving inline-image references in composed email HTML into URLs a browser
 * can actually fetch.
 *
 * Composed outbound HTML reaches the preview carrying references, not URLs, in
 * two shapes — and neither renders:
 *
 * 1. `<img src="file:123" data-file-id="123">` — what the Plate serializer emits
 *    (`plateEmailSerializer.ts`). `file:` is not in DOMPurify's allowed-scheme
 *    list, so the sanitizer in `EmailIframe` silently deletes the `src`.
 * 2. `<img data-file-id="123">` with no `src` at all — what a *stored signature*
 *    looks like. The CRM's nh3 allowlist (`event_normalizer.py`) permits only
 *    http/https/mailto/cid/tel, so the src is dropped on write by design and
 *    only `data-file-id` survives. This is why signature images in particular
 *    render broken.
 *
 * Either way the preview gets `<img>` tags with nothing to load. The send path
 * has its own resolver (`process_inline_image_placeholders`, which converts to
 * `cid:` + attachments) but it is deliberately skipped when composing a preview,
 * and `cid:` is unresolvable in a browser anyway.
 *
 * These helpers are pure and DOM-based rather than regex-based, which sidesteps
 * the `file:1` / `file:12` prefix-collision trap. They follow the same
 * detached-container idiom as `linkSafety.ts`.
 */

/** Shape of the file-detail endpoint's response that we care about. */
export interface InlineImageFileDetail {
  file_url?: string | null;
  download_url?: string | null;
}

/**
 * The file id an `<img>` refers to, or null if it isn't an inline-file image.
 *
 * Prefers `data-file-id` (present on both shapes above) and falls back to
 * parsing a `file:` src. The src pattern is anchored so `file:1` cannot match
 * an element referring to file 12.
 */
function readFileId(img: Element): string | null {
  const attr = img.getAttribute('data-file-id');
  if (attr != null && /^\d+$/.test(attr)) {
    return attr;
  }
  const match = /^file:(\d+)$/.exec(img.getAttribute('src') ?? '');
  return match?.[1] ?? null;
}

/** Every distinct file id referenced by an inline image in `html`. */
export function extractInlineImageFileIds(html: string): string[] {
  if (html === '') {
    return [];
  }
  const container = document.createElement('div');
  container.innerHTML = html;

  const ids = new Set<string>();
  container.querySelectorAll('img').forEach(img => {
    const id = readFileId(img);
    if (id != null) {
      ids.add(id);
    }
  });
  return Array.from(ids);
}

/**
 * Rewrite each inline image's `src` to its resolved URL.
 *
 * An image whose id has no resolved URL yet (still loading, or the fetch failed)
 * has any `file:` placeholder src stripped, so the browser does not attempt a
 * doomed `file:123` request and log a console error. It renders as a broken
 * image either way, but quietly.
 */
export function applyResolvedInlineImages(
  html: string,
  urlByFileId: Readonly<Record<string, string>>
): string {
  if (html === '') {
    return '';
  }
  const container = document.createElement('div');
  container.innerHTML = html;

  let changed = false;
  container.querySelectorAll('img').forEach(img => {
    const id = readFileId(img);
    if (id == null) {
      return;
    }
    const url = urlByFileId[id];
    if (url != null && url !== '') {
      img.setAttribute('src', url);
      changed = true;
      return;
    }
    if ((img.getAttribute('src') ?? '').startsWith('file:')) {
      img.removeAttribute('src');
      changed = true;
    }
  });

  return changed ? container.innerHTML : html;
}
